import { createStore } from 'vuex'
import { auth, db } from '../firebase'
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
} from 'firebase/auth'
import {
  collection,
  doc,
  setDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore'
import router from '../router'

// ── Exercices par défaut ───────────────────────────────────
const MUSCLE_EXERCISES = [
  'Appareil pour les quadriceps',
  'Appareil pour les ischio-jambiers',
  'Push-up',
  'Tirade',
  'Dips',
  'Flexion des coudes',
  'Appareil pour les épaules',
  'Appareil pour les abdominaux',
]

function makeEmptySession(sessionNumber) {
  return {
    sessionNumber,
    date: '',
    warmup: { effort: '', duration: '' },
    cardio: { effort: '', duration: '' },
    flexibility: false,
    exercises: MUSCLE_EXERCISES.map(name => ({
      name, charge: '', reps: '', series: '',
    })),
  }
}

// ── Helpers Firestore ──────────────────────────────────────
function programmesRef() {
  return collection(db, 'programmes')
}
function sessionsRef(programmeId) {
  return collection(db, 'programmes', programmeId, 'sessions')
}
function sessionDoc(programmeId, sessionId) {
  return doc(db, 'programmes', programmeId, 'sessions', sessionId)
}

export default createStore({
  state: {
    // Auth
    user: null,

    // Programme actif
    programmeId: null,
    programmeName: '',

    // Sessions
    sessions: [],       // [{ id, sessionNumber, date, warmup, cardio, flexibility, exercises }]
    loadingSessions: false,
    savingSession: false,
    sessionError: null,
  },

  mutations: {
    // ── Auth ──
    SET_USER(state, user) {
      state.user = user
    },
    CLEAR_USER(state) {
      state.user = null
    },

    // ── Programme ──
    SET_PROGRAMME(state, { id, name }) {
      state.programmeId = id
      state.programmeName = name
    },
    CLEAR_PROGRAMME(state) {
      state.programmeId = null
      state.programmeName = ''
      state.sessions = []
    },

    // ── Sessions ──
    SET_SESSIONS(state, sessions) {
      state.sessions = sessions
    },
    ADD_SESSION(state, session) {
      state.sessions.push(session)
    },
    UPDATE_SESSION(state, { id, data }) {
      const idx = state.sessions.findIndex(s => s.id === id)
      if (idx !== -1) state.sessions[idx] = { ...state.sessions[idx], ...data }
    },
    REMOVE_SESSION(state, id) {
      state.sessions = state.sessions.filter(s => s.id !== id)
      // Renuméroter
      state.sessions.forEach((s, i) => { s.sessionNumber = i + 1 })
    },

    // ── Status ──
    SET_LOADING_SESSIONS(state, val) { state.loadingSessions = val },
    SET_SAVING_SESSION(state, val)   { state.savingSession = val },
    SET_SESSION_ERROR(state, msg)    { state.sessionError = msg },
  },

  actions: {
    // ────────────────────────────────────────────────────────
    // AUTH
    // ────────────────────────────────────────────────────────

    async login({ commit, dispatch }, { email, password }) {
      try {
        await signInWithEmailAndPassword(auth, email, password)
        commit('SET_USER', auth.currentUser)
        await dispatch('fetchOrCreateProgramme')
        router.push('/')
      } catch (error) {
        switch (error.code) {
          case 'auth/user-not-found': alert('Utilisateur introuvable'); break
          case 'auth/wrong-password': alert('Mot de passe incorrect'); break
          default: alert('Erreur de connexion')
        }
      }
    },

    async register({ commit, dispatch }, { email, password }) {
      try {
        await createUserWithEmailAndPassword(auth, email, password)
        commit('SET_USER', auth.currentUser)
        // Crée automatiquement un programme + 13 sessions pour le nouvel utilisateur
        await dispatch('creerProgramme', 'Mon programme Éconofitness')
        router.push('/')
      } catch (error) {
        switch (error.code) {
          case 'auth/email-already-in-use': alert('Email déjà utilisé'); break
          case 'auth/invalid-email':        alert('Email invalide'); break
          case 'auth/operation-not-allowed':alert('Opération non autorisée'); break
          case 'auth/weak-password':        alert('Mot de passe trop faible'); break
          default: alert('Erreur lors de l\'inscription')
        }
      }
    },

    async logout({ commit }) {
      await signOut(auth)
      commit('CLEAR_USER')
      commit('CLEAR_PROGRAMME')
      router.push('/login')
    },

    fetchUser({ commit, dispatch }) {
      auth.onAuthStateChanged(async user => {
        if (user === null) {
          commit('CLEAR_USER')
          commit('CLEAR_PROGRAMME')
        } else {
          commit('SET_USER', user)
          await dispatch('fetchOrCreateProgramme')
          if (router.isReady() && router.currentRoute.value.path === '/login') {
            router.push('/')
          }
        }
      })
    },

    // ────────────────────────────────────────────────────────
    // PROGRAMMES
    // ────────────────────────────────────────────────────────

    /**
     * Récupère le programme de l'utilisateur connecté.
     * S'il n'en a pas encore, en crée un automatiquement.
     */
    async fetchOrCreateProgramme({ commit, dispatch, state }) {
      if (!state.user) return
      try {
        const snap = await getDocs(
          query(
            collection(db, 'programmes'),
            orderBy('createdAt', 'desc')
          )
        )
        // Filtre les programmes appartenant à l'utilisateur connecté
        const mine = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(p => p.userId === state.user.uid)

        if (mine.length > 0) {
          const p = mine[0]
          commit('SET_PROGRAMME', { id: p.id, name: p.nom })
          await dispatch('recupererSessions')
        } else {
          await dispatch('creerProgramme', 'Mon programme Éconofitness')
        }
      } catch (e) {
        commit('SET_SESSION_ERROR', 'Erreur lors du chargement du programme : ' + e.message)
      }
    },

    /** Crée un nouveau programme et 13 sessions vides */
    async creerProgramme({ commit, dispatch, state }, nom) {
      if (!state.user) return
      const ref = await addDoc(programmesRef(), {
        nom,
        userId: state.user.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })
      commit('SET_PROGRAMME', { id: ref.id, name: nom })
      // Crée 13 sessions initiales
      for (let i = 1; i <= 13; i++) {
        await dispatch('ajouterSession')
      }
    },

    // ────────────────────────────────────────────────────────
    // SESSIONS
    // ────────────────────────────────────────────────────────

    /** Charge toutes les sessions du programme actif */
    async recupererSessions({ commit, state }) {
      if (!state.programmeId) return
      commit('SET_LOADING_SESSIONS', true)
      commit('SET_SESSION_ERROR', null)
      try {
        const snap = await getDocs(
          query(sessionsRef(state.programmeId), orderBy('sessionNumber', 'asc'))
        )
        const sessions = snap.docs.map(d => ({ id: d.id, ...d.data(), _saved: true }))
        commit('SET_SESSIONS', sessions)
      } catch (e) {
        commit('SET_SESSION_ERROR', 'Impossible de charger les sessions : ' + e.message)
      } finally {
        commit('SET_LOADING_SESSIONS', false)
      }
    },

    /**
     * Sauvegarde (crée ou met à jour) une session.
     * @param {Object} sessionData  — données de la session (doit contenir id si update)
     */
    async sauvegarderSession({ commit, state }, sessionData) {
      if (!state.programmeId) return
      commit('SET_SAVING_SESSION', true)
      commit('SET_SESSION_ERROR', null)
      try {
        const { id, ...payload } = sessionData
        payload.updatedAt = serverTimestamp()

        if (id) {
          // Mise à jour
          await setDoc(sessionDoc(state.programmeId, id), payload, { merge: true })
          commit('UPDATE_SESSION', { id, data: { ...payload, _saved: true } })
        } else {
          // Création
          const ref = await addDoc(sessionsRef(state.programmeId), payload)
          commit('UPDATE_SESSION', { id: ref.id, data: { id: ref.id, _saved: true } })
        }
        // Met à jour updatedAt du programme
        await updateDoc(doc(db, 'programmes', state.programmeId), {
          updatedAt: serverTimestamp(),
        })
      } catch (e) {
        commit('SET_SESSION_ERROR', 'Erreur lors de la sauvegarde : ' + e.message)
        throw e
      } finally {
        commit('SET_SAVING_SESSION', false)
      }
    },

    /** Ajoute une session vide à la fin */
    async ajouterSession({ commit, state }) {
      if (!state.programmeId) return
      const nextNumber = state.sessions.length + 1
      const payload = {
        ...makeEmptySession(nextNumber),
        updatedAt: serverTimestamp(),
      }
      try {
        const ref = await addDoc(sessionsRef(state.programmeId), payload)
        commit('ADD_SESSION', { id: ref.id, ...payload, _saved: true })
      } catch (e) {
        commit('SET_SESSION_ERROR', 'Impossible d\'ajouter une session : ' + e.message)
        throw e
      }
    },

    /** Supprime une session et renumérote les suivantes */
    async supprimerSession({ commit, dispatch, state }, sessionId) {
      if (!state.programmeId) return
      try {
        await deleteDoc(sessionDoc(state.programmeId, sessionId))
        commit('REMOVE_SESSION', sessionId)
        // Resynchronise les numéros en base
        await dispatch('resyncSessionNumbers')
      } catch (e) {
        commit('SET_SESSION_ERROR', 'Impossible de supprimer la session : ' + e.message)
        throw e
      }
    },

    /** Remet à jour sessionNumber dans Firestore après une suppression */
    async resyncSessionNumbers({ state }) {
      if (!state.programmeId) return
      await Promise.all(
        state.sessions.map((s, i) =>
          updateDoc(sessionDoc(state.programmeId, s.id), { sessionNumber: i + 1 })
        )
      )
    },
  },

  getters: {
    isAuthenticated: state => state.user !== null,
    sessionCount:    state => state.sessions.length,
    getSessionById:  state => id => state.sessions.find(s => s.id === id),
  },
})